import { Fragment, useEffect, useRef, useState } from 'react';
import { ChevronRight, ContentCopy, ShoppingCart, AutoAwesome, RestartAlt } from '@mui/icons-material';

import { useAppDispatch, useAppSelector } from '../../../reducers/hooks';
import { set as setMaterial } from '../../../reducers/materialReducer';
import { set as setTool } from '../../../reducers/toolReducer';

import { Api } from '../../../App';

import './ObjectItem.css';
import { ObjectSchema, ObjectType } from '../ObjectBrowser';
import { MachineState } from '../../../reducers/machineReducer';

function getDisplayAttributes(type: ObjectType, object: ObjectSchema) : Array<[string, any]> {
    let attributes = object.attributes as any;
    switch (type) {
        case ObjectType.MATERIAL:
            return [
                ['Condition', attributes.condition || 'N/A'],
                ['Hardness', attributes.hardness_value === null ? 'N/A' : `${attributes.hardness_value} ${attributes.hardness_scale}`],
                ['Tensile Strength', attributes.tensile_strength_psi === null ? 'N/A' : `${Math.round(attributes.tensile_strength_psi / 1000)} ${Api.displayPreferredUnits('pressure')}`],
            ]
        case ObjectType.ENDMILL:
            return [
                ['Flutes', attributes.flutes],
                ['Diameter', `${attributes.diameter} ${Api.displayPreferredUnits('length')}`],
                ['Cut Length', `${attributes.cutLength} ${Api.displayPreferredUnits('length')}`],
                ['Corner Rad', `${attributes.cornerRadius} ${Api.displayPreferredUnits('length')}`],
            ]
        case ObjectType.DRILL:
            return [
                ['Flutes', attributes.flutes],
                ['Diameter', `${attributes.diameter} ${Api.displayPreferredUnits('length')}`],
                ['Cut Length', `${attributes.cutLength} ${Api.displayPreferredUnits('length')}`],
            ]
        default:
            return [];
    }
}

export function EditableAttributeNumber(
    {label, key, attributes, setAttributes, originalAttributes, unit, validation, style, defaultZero}:
    {label: string, key: string, attributes: any, setAttributes: any, originalAttributes: any, unit?: string, validation?: (value: number) => string, style?: any, defaultZero?: boolean}
    ) {
    const [draft, setDraft] = useState<any>(defaultZero ? 0 : attributes[key]);

    // If defaultZero is true, set the initial value to 0;
    // This way, the user will have to manually input a value, but can reset if desired
    let errorMsg = validation ? validation(isNaN(parseFloat(draft)) ? draft : parseFloat(draft)) : '';

    useEffect(() => {
        if (!defaultZero) setDraft(attributes[key]);
    }, [defaultZero, attributes, key])

    useEffect(() => {
        if (defaultZero) setDraft(0);
      }, []);      

    function updateAttributes(e: any) {
        let inputValue = parseFloat((e.target as any).value);
        if (inputValue) {
            if (validation && validation(inputValue) !== "") return;

            setAttributes({...attributes, [key]: inputValue})
        }
    }
    
    return <div className="editableAttribute" style={style}>
        <label>{label}</label>
        <input
            type="number"
            className={errorMsg ? 'error' : undefined}
            onChange={(e) => setDraft(e.target.value)}
            onKeyUp={updateAttributes}
            onBlur={() => setAttributes({...attributes, [key]: parseFloat(draft)})}
            value={draft}
        />
        {unit && <span>{unit}</span>}
        {parseFloat(draft) !== originalAttributes[key] &&
            <button
                className="resetBtn"
                onClick={() => {
                    setAttributes({...attributes, [key]: originalAttributes[key]});
                    setDraft(originalAttributes[key]);
                    errorMsg = '';
                }}>
                <RestartAlt />
            </button>
        }
        {errorMsg && <div className="error">{errorMsg}</div>}
    </div>
}

function EditableAttributeHardness({attributes, setAttributes, validation}: {attributes: any, setAttributes: any, validation?: (hardness: number, tensileStrength: number) => string}) {
    const [hardnessDraft, setHardnessDraft] = useState<any>(attributes.hardness_value);
    const [scale, setScale] = useState<any>(attributes.hardness_scale);

    const [tensileDraft, setTensileDraft] = useState<any>(attributes.tensile_strength_psi);

    const [allScalesDraft, setAllScalesDraft] = useState<any>({
        HBS: attributes.HBS || null,
        HBW: attributes.HBW || null,
        HRB: attributes.HRB || null,
        HRC: attributes.HRC || null,
        UTS: attributes.UTS || null
    });

    let errorMsg = validation ? validation(
        isNaN(parseFloat(hardnessDraft)) ? attributes.hardness_value : parseFloat(hardnessDraft),
        isNaN(parseFloat(tensileDraft)) ? attributes.tensile_strength_psi : parseFloat(tensileDraft),
    ) : '';

    const onBlur = async () => {
        // request backend for new hardness values
        if (parseFloat(hardnessDraft) !== attributes.hardness_value) {
            setAttributes({...attributes, hardness_value: parseFloat(hardnessDraft)});

            let newScales = await Api.getHardnessConversions(parseFloat(hardnessDraft), scale);

            setAllScalesDraft(newScales);
            if (newScales.UTS) {
                let newTensileStrength = Math.round(newScales.UTS / attributes.UTSRatio);
                setTensileDraft(newTensileStrength / 1000); // KSI
                // Set attributes so they can be reset later
                setAttributes({...attributes, hardness_value: parseFloat(hardnessDraft), tensile_strength_psi: newTensileStrength})
            }
        }
    }

    const onScaleChange = (e:any) => {
        setScale(e.target.value);
        setHardnessDraft(allScalesDraft[e.target.value]);
        setAttributes({...attributes, hardness_value: allScalesDraft[e.target.value], hardness_scale: e.target.value})
    }

    let hardnessValue = attributes.hardness_value;
    let scaleValue = attributes.hardness_scale;
    let tensileValue = attributes.tensile_strength_psi / 1000;

    useEffect(() => {
        setHardnessDraft(hardnessValue);
    }, [setHardnessDraft, hardnessValue]);

    useEffect(() => {
        setScale(scaleValue);
    }, [setScale, scaleValue]);

    useEffect(() => {
        setTensileDraft(tensileValue);
    }, [setTensileDraft, tensileValue]);
    
    return <>
        <div className="editableAttribute">
            <label>Hardness</label>
            <input
                type="number"
                onChange={(e) => setHardnessDraft(e.target.value)}
                onBlur={() => onBlur()}
                value={hardnessDraft}
            />
            <select
                onChange={onScaleChange}
                value={scale}
                title="Convert to a certain hardness scale"
            >
                <option value="HBS" disabled={allScalesDraft.HBS === null}>HBS (Brinell 10/500) {allScalesDraft.HBS === null && '[No conversion available]'}</option>
                <option value="HBW" disabled={allScalesDraft.HBW === null}>HBW (Brinell 10/3000) {allScalesDraft.HBW === null && '[No conversion available]'}</option>
                <option value="HRB" disabled={allScalesDraft.HRB === null}>HRB (Rockwell B) {allScalesDraft.HRB === null && '[No conversion available]'}</option>
                <option value="HRC" disabled={allScalesDraft.HRC === null}>HRC (Rockwell C) {allScalesDraft.HRC === null && '[No conversion available]'}</option>
            </select>
        </div>
        <div className="editableAttribute">
            <label>Tensile Strength</label>
            <input
                type="number"
                onChange={(e) => setTensileDraft(e.target.value)}
                onBlur={() => setAttributes({...attributes, tensile_strength_psi: parseFloat(tensileDraft) * 1000})}
                value={tensileDraft}
            />
            <span>{Api.displayPreferredUnits('pressure')}</span>
        </div>
        {errorMsg && <div className="error">{errorMsg}</div>}
    </>
}

function getEditableAttributes(type: ObjectType, machine: MachineState, attributes: any, setAttributes: any, originalAttributes: any, overrideZeroes: boolean) {
    switch (type) {
        case ObjectType.MATERIAL:
            return (
                <div className="editableAttributes">
                    {EditableAttributeHardness({
                        attributes,
                        setAttributes,
                        validation: (hardness, tensileStrength) => hardness <= 0 ?
                            'Hardness must be greater than 0' :
                                tensileStrength <= 0 ?
                                    'Tensile strength must be greater than 0' : ''
                    })}
                </div>
            )
        case ObjectType.ENDMILL:
        case ObjectType.DRILL:
            return (
                <div className="editableAttributes">
                    {EditableAttributeNumber({
                        label: 'Diameter',
                        key: 'diameter',
                        attributes,
                        setAttributes,
                        originalAttributes,
                        unit: Api.displayPreferredUnits('length'),
                        validation: (value) => value <= 0 ?
                            'Diameter must be greater than 0' : ''
                    })}
                    {EditableAttributeNumber({
                        label: 'Flutes',
                        key: 'flutes',
                        attributes,
                        setAttributes,
                        originalAttributes,
                        validation: (value) => value <= 0 ?
                            'Flutes must be greater than 0' :
                                value !== Math.round(value) ?
                                    'Flutes must be an integer' : ''
                    })}
                    {EditableAttributeNumber({
                        label: 'Len. of Cut',
                        key: 'cutLength',
                        attributes,
                        setAttributes,
                        originalAttributes,
                        unit: Api.displayPreferredUnits('length'),
                        validation: (value) => value <= 0 ?
                            'Len. of Cut must be greater than 0' : ''
                    })}
                    <hr />
                    <p>Enter parameters below or use defaults. Then, press "Calculate".</p>
                    {EditableAttributeNumber({
                        label: 'Tool Stickout',
                        key: 'toolStickout',
                        attributes,
                        setAttributes,
                        originalAttributes,
                        unit: Api.displayPreferredUnits('length'),
                        defaultZero: !overrideZeroes,
                        validation: (value) => value <= 0 ?
                            'Tool Stickout must be greater than 0' : ''
                    })}
                    {type === ObjectType.ENDMILL &&
                        <Fragment>
                            {EditableAttributeNumber({
                                label: 'Depth of Cut',
                                key: 'depthOfCut',
                                attributes,
                                setAttributes,
                                originalAttributes,
                                unit: Api.displayPreferredUnits('length'),
                                defaultZero: !overrideZeroes,
                                validation: (value) => value <= 0 ?
                                    'Depth of Cut must be greater than 0' :
                                        value > attributes.cutLength ?
                                            'Depth of Cut may not exceed Len. of Cut' : ''
                    })}
                            {EditableAttributeNumber({
                                label: 'Width of Cut',
                                key: 'widthOfCut',
                                attributes,
                                setAttributes,
                                originalAttributes,
                                unit: Api.displayPreferredUnits('length'),
                                defaultZero: !overrideZeroes,
                                validation: (value) => value <= 0 ?
                                    'Width of Cut must be greater than 0' : ''
                    })}
                            {EditableAttributeNumber({
                                    label: 'Width of Slot',
                                    key: 'widthOfSlot',
                                    attributes,
                                    setAttributes,
                                    originalAttributes,
                                    unit: Api.displayPreferredUnits('length'),
                                    defaultZero: !overrideZeroes,
                                    validation: (value) => {
                                        // Prevent error detection from occurring when this option is hidden
                                        if (!(machine.toolPath === "arc")) return '';
                                        return value <= 0 ? 'Width of Slot must be greater than 0' : ''
                                    },
                                    // If this attribute is removed altogether, we get a state error (the amount of hooks change between renders).
                                    // Hiding it fixes that issue.
                                    // There are probably better ways of doing this, but it works...
                                    style: (machine.toolPath === "arc") ? undefined : {display: 'none'}
                                })
                            }
                        </Fragment>
                    }
                    {type === ObjectType.DRILL &&
                        EditableAttributeNumber({
                            label: 'Depth of Hole',
                            key: 'depthOfHole',
                            attributes,
                            setAttributes,
                            originalAttributes,
                            defaultZero: !overrideZeroes,
                            validation: (value) => value <= 0 ?
                                'Depth of Hole must be greater than 0' :
                                    value > attributes.cutLength ?
                                        'Depth of Hole may not exceed Len. of Cut' : ''
                        })
                    }
                </div>
            );
        default:
            return [];
    }
}


function ObjectItem(
    { object, changeStep, type, open, setOpen } : {object: ObjectSchema, changeStep: any, type: ObjectType, open: boolean, setOpen: any}
)
{
    const [tempAttributes, setTempAttributes] = useState<any>(object.attributes);
    const [isError, setIsError] = useState<boolean>(false);
    const [machine, tool] = useAppSelector(state => [state.machine, state.tool]);
    const dispatch = useAppDispatch();
    const [overrideZeroes, setOverrideZeroes] = useState(false);
    const objectBodyRef = useRef<HTMLDivElement>(null);


    // Definitely not best practice, but it's the easiest way to do this without doing a bunch of
    // passing up state from the editable attribute components.
    const handleClickOutside = () => {
        setTimeout(() => {
            if (objectBodyRef.current) {
                let newError = objectBodyRef.current.querySelector('.error') !== null;
                if (isError !== newError) setIsError(newError);
            }
        }, 0)
    }

    useEffect(() => {
        document.addEventListener('mouseup', handleClickOutside);
        document.addEventListener('keyup', handleClickOutside);

        return () => {
            document.removeEventListener('mouseup', handleClickOutside);
            document.removeEventListener('keyup', handleClickOutside);
        }
    })


    const displayAttributes = getDisplayAttributes(type, object).map((attribute, i) =>
        <div className="objectAttribute" key={i}>
            <div className="attributeName">{attribute[0]}</div>
            <div className="attributeValue">{attribute[1]}</div>
        </div>
    )

    const nextStep = () => {
        if (type === ObjectType.MATERIAL) {
            dispatch(setMaterial({
                materialGroup: tempAttributes?.machining_group,
                tensileStrength: tempAttributes?.tensile_strength_psi || 0,
                hardness: tempAttributes?.hardness_value || 0,
                hardnessScale: tempAttributes?.hardness_scale || "HBS",

                id: tempAttributes?.id || 0,
                name: object.name || 'Custom Material',
                description: object.description || ''
            }))
        } else if (type === ObjectType.ENDMILL || type === ObjectType.DRILL) {
            dispatch(setTool({
                type: ObjectType.ENDMILL === type ? 'endmill' : 'drill',
                diameter: tempAttributes?.diameter || 0,
                flutes: tempAttributes?.flutes || 0,
                cutLength: tempAttributes?.cutLength || 0,
                coating: tempAttributes?.coating || '',
                toolPath: tempAttributes?.toolPath || "hem",
                helixAngle: tempAttributes?.helixAngle || 0,
				geometry: tempAttributes?.geometry || 0,

                toolStickout: tempAttributes?.toolStickout || 0,
                depthOfCut: tempAttributes?.depthOfCut || 0,
                widthOfCut: tempAttributes?.widthOfCut || 0,
                widthOfSlot: tempAttributes?.widthOfSlot || 0,
                depthOfHole: tempAttributes?.depthOfHole || 0,

                edp: tempAttributes?.EDP || 0,
                name: object.name,
                series: tempAttributes?.product || '',

                speedFinetuning: object.name === tool.name ? tool.speedFinetuning : 1,
                feedFinetuning: object.name === tool.name ? tool.feedFinetuning : 1,
            }))
        }
        changeStep('+');
    }
        
    return (
        <div
            className={object.attributes.isSuggestion ? "object suggestion" : "object"}
            key={object.value}
        >
            <div className="objectInitial" onClick={() => setOpen(!open)}>
                {object.image ? <div className="imgOuter"><img src={object.image} alt="Tool Preview" /></div> : null}
                {object.attributes.isSuggestion && <AutoAwesome />}
                <div className="objectName">{object.name}</div>
                {object.description ? <div title={object.description} className="objectDescription">{object.description}</div> : null}
                {displayAttributes}
                <ChevronRight style={{transform: open ? 'rotate(90deg)' : ''}} />
            </div>
            <div className={`objectBody ${open && 'open'}`} ref={objectBodyRef}>
                {!object.attributes.isSuggestion && <p><b>{object.name}</b> {object.description}</p>}
                <div className="objectAttributes">{displayAttributes}</div>
                <div className="btnrow">
                    {!object.attributes.isSuggestion && <button className="btn primaryBtn" onClick={() => window.navigator.clipboard.writeText(`${object.name} - ${object.description}`)}>
                        <ContentCopy />
                        <span>Copy</span>
                    </button>}
                    {type !== ObjectType.MATERIAL && 
                        <a
                            className="btn primaryBtn"
                            href={`https://www.garrtool.com/product-details/?EDP=${object.attributes.EDP}`}
                            target="_blank"
                            rel="noreferrer">
                            <ShoppingCart />
                            <span>View and Buy</span>
                        </a>
                    }
                </div>
                <h3>Customize</h3>
                {getEditableAttributes(type, machine, tempAttributes, setTempAttributes, object.attributes, overrideZeroes)}
                <div className="btnrow">
                    <button className="btn dangerBtn" onClick={() => { setOverrideZeroes(true); setTempAttributes(object.attributes); setIsError(false) } }>
                        <span>{type !== ObjectType.MATERIAL ? 'Use Default Values' : 'Reset Values'}</span>
                    </button>
                    <button className={isError ? "btn disabledBtn" : "btn primaryBtn"} onClick={() => isError ? null : nextStep()}>
                        <span>{type !== ObjectType.MATERIAL ? 'Calculate' : 'Continue'}</span>
                        <ChevronRight />
                    </button>
                </div>
                
            </div>
        </div>
    )
}

export default ObjectItem;